export type AffiliatePriority = "high" | "medium" | "low";

export interface AffiliateCampaignOverride {
  /** Sidste dag kampagnen er aktiv (inklusiv), ISO YYYY-MM-DD */
  endsAt: string;
  advertiser?: string;
  category?: string;
  url?: string;
  defaultCta?: string;
  allowedContexts?: string[];
  priority?: AffiliatePriority;
}

export interface AffiliateLinkConfig {
  id: string;
  network: string;
  advertiser: string;
  category: string;
  url: string;
  defaultCta: string;
  allowedContexts: string[];
  priority: AffiliatePriority;
  campaign?: AffiliateCampaignOverride;
  /** Regelbaseret målretning — tomme arrays = matcher alle. */
  allowedBrandIds?: string[];
  allowedModelIds?: string[];
  /** Sprog artiklen skal være på for at annoncen kan vises. Default ['da']. */
  languages?: string[];
  /** Enheder annoncen må vises på. Default ['desktop','mobile']. */
  devices?: Array<'desktop' | 'mobile'>;
}

export const affiliateLinks = {
  leasyMinilaan: {
    id: "leasy-minilaan",
    network: "adtraction",
    advertiser: "L'EASY Minilån DK",
    category: "finansiering",
    url: "https://go.adt228.com/t/t?a=1074819334&as=2082817320&t=2&tk=1",
    defaultCta: "Se lånemuligheder hos L'EASY",
    allowedContexts: ["finansiering", "billån", "privatlån", "økonomi", "køb-af-elbil", "leasing"],
    priority: "high",
  },
  paymarkFinans: {
    id: "paymark-finans",
    network: "adtraction",
    advertiser: "PayMark Finans",
    category: "finansiering",
    url: "https://go.adt284.net/t/t?a=1248510061&as=2082817320&t=2&tk=1",
    defaultCta: "Se finansieringsmuligheder hos PayMark Finans",
    allowedContexts: ["finansiering", "billån", "privatlån", "økonomi", "køb-af-elbil", "leasing"],
    priority: "high",
  },
  facitBankLaan: {
    id: "facit-bank-laan",
    network: "adtraction",
    advertiser: "Facit Bank",
    category: "finansiering",
    url: "https://go.adt231.net/t/t?a=1820677675&as=2082817320&t=2&tk=1",
    defaultCta: "Se lånemuligheder hos Facit Bank",
    allowedContexts: ["finansiering", "billån", "privatlån", "økonomi", "køb-af-elbil", "leasing"],
    priority: "high",
  },
  snoutie: {
    id: "snoutie",
    network: "adtraction",
    advertiser: "Snoutie",
    category: "kaeledyr",
    url: "https://go.adt231.net/t/t?a=2069773785&as=2082817320&t=2&tk=1",
    defaultCta: "Se produkter hos Snoutie",
    allowedContexts: ["hund-i-bil", "kat-i-bil", "kaeledyr-i-elbil", "bilferie-med-hund", "tilbehoer-til-kaeledyr"],
    priority: "low",
  },
  tjenestetorvetSolceller: {
    id: "tjenestetorvet-solceller",
    network: "adtraction",
    advertiser: "Tjenestetorvet Solceller",
    category: "solceller",
    url: "https://go.adt212.net/t/t?a=1994032443&as=2082817320&t=2&tk=1",
    defaultCta: "Få tilbud på solceller",
    allowedContexts: ["solceller", "hjemmeopladning", "strom-til-elbil", "elbil-og-solceller", "økonomi"],
    priority: "medium",
  },
  tjenestetorvetStroem: {
    id: "tjenestetorvet-stroem",
    network: "adtraction",
    advertiser: "Tjenestetorvet Strøm",
    category: "stroem",
    url: "https://go.adt228.com/t/t?a=1994032074&as=2082817320&t=2&tk=1",
    defaultCta: "Sammenlign elaftaler hos Tjenestetorvet",
    allowedContexts: ["stroem", "elpris", "hjemmeopladning", "opladning", "billig-stroem-til-elbil", "økonomi"],
    priority: "high",
  },
  tjenestetorvetForsikring: {
    id: "tjenestetorvet-forsikring",
    network: "adtraction",
    advertiser: "Tjenestetorvet Forsikring",
    category: "forsikring",
    url: "https://go.adt267.com/t/t?a=1994034032&as=2082817320&t=2&tk=1",
    defaultCta: "Få tilbud på forsikring",
    allowedContexts: ["forsikring", "bilforsikring", "elbilforsikring", "økonomi", "køb-af-elbil"],
    priority: "high",
  },
  daekOnline: {
    id: "daek-online",
    network: "adtraction",
    advertiser: "Dæk-online",
    category: "daek",
    url: "https://on.daek-online.dk/t/t?a=1328220007&as=2082817320&t=2&tk=1",
    defaultCta: "Se dæk hos Dæk-online",
    allowedContexts: ["daek", "vinterdaek", "sommerdaek", "helaarsdaek", "elbildaek", "tilbehoer"],
    priority: "high",
  },
  bornfiber: {
    id: "bornfiber",
    network: "adtraction",
    advertiser: "Bornfiber",
    category: "internet",
    url: "https://pin.bornfiber.dk/t/t?a=1832135802&as=2082817320&t=2&tk=1",
    defaultCta: "Se fiberinternet hos Bornfiber",
    allowedContexts: ["internet", "hjem", "smarthome", "opladning-apps"],
    priority: "low",
  },
  flexiiMobil: {
    id: "flexii-mobil",
    network: "adtraction",
    advertiser: "Flexii",
    category: "mobilabonnement",
    url: "https://go.adt256.com/t/t?a=1751759538&as=2082817320&t=2&tk=1",
    defaultCta: "Se mobilabonnement hos Flexii",
    allowedContexts: ["mobilabonnement", "apps", "bil-apps", "connectivity", "hjem"],
    priority: "low",
  },
  hiperInternet: {
    id: "hiper-internet",
    network: "adtraction",
    advertiser: "Hiper",
    category: "internet",
    url: "https://go.adt242.com/t/t?a=1697235826&as=2082817320&t=2&tk=1",
    defaultCta: "Se internet hos Hiper",
    allowedContexts: ["internet", "hjem", "smarthome", "opladning-apps"],
    priority: "low",
  },
  ewiiInternet: {
    id: "ewii-internet",
    network: "adtraction",
    advertiser: "EWII Internet",
    category: "internet",
    url: "https://go.ewii.dk/t/t?a=1693093225&as=2082817320&t=2&tk=1",
    defaultCta: "Se internet hos EWII",
    allowedContexts: ["internet", "hjem", "smarthome", "opladning-apps"],
    priority: "low",
  },
  trodo: {
    id: "trodo",
    network: "adtraction",
    advertiser: "Trodo",
    category: "reservedele",
    url: "https://on.trodo.dk/t/t?a=1985915127&as=2082817320&t=2&tk=1",
    defaultCta: "Se reservedele hos Trodo",
    allowedContexts: ["reservedele", "bildele", "tilbehoer", "service", "vedligehold", "daek"],
    priority: "medium",
  },
  etoro: {
    id: "etoro",
    network: "adtraction",
    advertiser: "eToro",
    category: "investering",
    url: "https://go.adt231.net/t/t?a=1889284613&as=2082817320&t=2&tk=1",
    defaultCta: "Invester hos eToro",
    allowedContexts: ["investering", "økonomi", "privatøkonomi", "opsparing"],
    priority: "medium",
  },
  one2move: {
    id: "one2move",
    network: "adtraction",
    advertiser: "One2Move Biludlejning",
    category: "biludlejning",
    url: "https://go.one2movebiludlejning.dk/t/t?a=1876544623&as=2082817320&t=2&tk=1",
    defaultCta: "Lej bil hos One2Move",
    allowedContexts: ["biludlejning", "leje-af-bil", "ferie", "rejse", "transport", "elbil-leje"],
    priority: "medium",
  },
  minAkasse: {
    id: "min-a-kasse",
    network: "adtraction",
    advertiser: "Min A-kasse",
    category: "a-kasse",
    url: "https://go.min-a-kasse.dk/t/t?a=1667704485&as=2082817320&t=2&tk=1",
    defaultCta: "Se medlemskab hos Min A-kasse",
    allowedContexts: ["økonomi", "forsikring", "privatøkonomi", "tryghed"],
    priority: "low",
  },
  carpardoo: {
    id: "carpardoo",
    network: "adtraction",
    advertiser: "Carpardoo",
    category: "reservedele",
    url: "https://go.adt256.com/t/t?a=1335174330&as=2082817320&t=2&tk=1",
    defaultCta: "Se reservedele hos Carpardoo",
    allowedContexts: ["reservedele", "bildele", "tilbehoer", "service", "vedligehold", "daek"],
    priority: "medium",
  },
  tirendo: {
    id: "tirendo",
    network: "adtraction",
    advertiser: "Tirendo",
    category: "daek",
    url: "https://in.tirendo.dk/t/t?a=1280621416&as=2082817320&t=2&tk=1",
    defaultCta: "Se dæk hos Tirendo",
    allowedContexts: ["daek", "vinterdaek", "sommerdaek", "helaarsdaek", "elbildaek", "tilbehoer"],
    priority: "high",
  },
  hejOscar: {
    id: "hej-oscar",
    network: "adtraction",
    advertiser: "Hej Oscar",
    category: "rejseforsikring",
    url: "https://go.hejoscar.dk/t/t?a=1876544899&as=2082817320&t=2&tk=1",
    defaultCta: "Se Hej Oscar rejseforsikring",
    allowedContexts: ["forsikring", "rejseforsikring", "ferie", "rejse", "bilferie", "tryghed"],
    priority: "medium",
  },
  bluemoon: {
    id: "bluemoon",
    network: "adtraction",
    advertiser: "Bluemoon",
    category: "rejser",
    url: "https://ion.bluemoon.dk/t/t?a=2081305461&as=2082817320&t=2&tk=1",
    defaultCta: "Se rejser hos Bluemoon",
    allowedContexts: ["rejse", "ferie", "bilferie", "rejseforsikring"],
    priority: "low",
    campaign: {
      endsAt: "2026-07-31",
      advertiser: "Blue Moon",
      category: "boligindretning",
      defaultCta: "Op til 30% på futon + sengeramme hos Blue Moon",
      allowedContexts: ["hjem", "boligindretning", "livsstil", "soveværelse"],
      priority: "medium",
    },
  },
  oisterInternet: {
    id: "oister-internet",
    network: "adtraction",
    advertiser: "OiSTER",
    category: "internet",
    url: "https://go.adt284.net/t/t?a=1666103641&as=2082817320&t=2&tk=1",
    defaultCta: "Se 5G-internet hos OiSTER",
    allowedContexts: ["internet", "hjem", "smarthome", "opladning-apps", "mobilabonnement"],
    priority: "medium",
    campaign: {
      endsAt: "2026-08-03",
      defaultCta: "12 mdr. til 199 kr./md hos OiSTER — spar 960 kr.",
    },
  },
  capitalbox: {
    id: "capitalbox",
    network: "adtraction",
    advertiser: "CapitalBox",
    category: "erhvervslån",
    url: "https://go.adt267.com/t/t?a=1808759800&as=2082817320&t=2&tk=1",
    defaultCta: "Søg erhvervslån hos CapitalBox",
    allowedContexts: ["finansiering", "erhvervslån", "økonomi", "virksomhed", "billån"],
    priority: "medium",
  },
  creditstar: {
    id: "creditstar",
    network: "adtraction",
    advertiser: "Creditstar",
    category: "finansiering",
    url: "https://on.creditstar.dk/t/t?a=1895247647&as=2082817320&t=2&tk=1",
    defaultCta: "Se lånemuligheder hos Creditstar",
    allowedContexts: ["finansiering", "billån", "privatlån", "økonomi", "køb-af-elbil"],
    priority: "medium",
  },
  mofibo: {
    id: "mofibo",
    network: "adtraction",
    advertiser: "Mofibo",
    category: "lydboeger",
    url: "https://go.adt212.net/t/t?a=1400643923&as=2082817320&t=2&tk=1",
    defaultCta: "Prøv Mofibo gratis",
    allowedContexts: ["lydboeger", "podcast", "underholdning", "roadtrip", "bilferie", "rejse", "lyd-til-turen"],
    priority: "medium",
  },
} satisfies Record<string, AffiliateLinkConfig>;

export type AffiliateId = keyof typeof affiliateLinks;

// getAffiliateLink lever i @/lib/affiliate-registry — importér derfra.
// Denne fil eksporterer kun seed-data + typer for at undgå cirkulær import.

